import React from 'react';
import { CheckCircle2, KeyRound, Plus, Trash2 } from 'lucide-react';
import { Input } from '../../ui/Input';
import { Select } from '../../ui/Select';
import { Textarea } from '../../ui/Textarea';
import { Button } from '../../ui/Button';
import { SearchableSelect } from '../../ui/SearchableSelect';
import { CreatableSelect } from '../../ui/CreatableSelect';
import { ChoiceGroup } from '../ChoiceGroup';
import { useTrainingCourses } from '../../../hooks/useTrainingCourses';
import { usePractices } from '../../../hooks/usePractices';
import { practicesStore } from '../../../services/practicesStore';
import { practiceDeliveryModes } from '../../../data/practiceOptions';
import { formatDateTime, isInPresence, newId } from '../../../utils/practices';
import { PracticeCourseStatus, PracticeDeliveryMode, PracticeDraft, PracticeErrors, PracticeParticipant } from '../../../types/practice';
interface PracticeSchedulingProps {
  draft: PracticeDraft;
  onChange: (changes: Partial<PracticeDraft>) => void;
  errors: PracticeErrors;
  onConfirm: () => void;
}
export function PracticeScheduling({
  draft,
  onChange,
  errors,
  onConfirm
}: PracticeSchedulingProps) {
  const courses = useTrainingCourses();
  const {
    extraCourseCodes
  } = usePractices();
  const inPresence = isInPresence(draft);
  const confirmed = draft.schedulingConfirmed;

  // In-presence: auto-populated from the courses already inserted
  const presenceCodes = Array.from(new Set([...courses.filter(c => c.status !== 'ondemand').map(c => c.courseCode), ...extraCourseCodes]));
  // On-demand: the course code already exists on the delivery platform
  const elearningOptions = courses.filter(c => c.status === 'ondemand').map(c => ({
    value: c.courseCode,
    label: `${c.courseCode} — ${c.name}`
  }));
  const setMode = (mode: PracticeDeliveryMode) => onChange({
    deliveryMode: mode,
    courseCode: undefined,
    schedulingConfirmed: false,
    courseStatus: mode === 'elearning' ? 'On-demand' : 'Programmato'
  });
  const updateParticipant = (id: string, changes: Partial<PracticeParticipant>) => onChange({
    participants: draft.participants.map(p => p.id === id ? {
      ...p,
      ...changes
    } : p)
  });
  const addParticipant = () => onChange({
    participants: [...draft.participants, {
      id: newId('p'),
      firstName: '',
      lastName: '',
      email: ''
    }]
  });
  const removeParticipant = (id: string) => onChange({
    participants: draft.participants.filter(p => p.id !== id)
  });
  const sendCredentials = () => {
    const now = new Date().toISOString();
    onChange({
      participants: draft.participants.map(p => p.credentialsSentAt || !p.email ? p : {
        ...p,
        username: `${p.firstName.charAt(0)}.${p.lastName}`.toLowerCase().replace(/\s+/g, ''),
        credentialsSentAt: now
      })
    });
  };
  const pendingCredentials = draft.participants.filter(p => !p.credentialsSentAt).length;
  return <div className="space-y-6" data-id="element-1452" data-mp="yj6d3ea8q4f0">
      <ChoiceGroup label="Tipo di corso" name="practice-mode" value={draft.deliveryMode || '' as PracticeDeliveryMode} options={practiceDeliveryModes} onChange={setMode} data-id="element-1453" data-mp="yidb0qm88337" />
      {errors.deliveryMode && <p className="text-xs text-red-600 font-medium -mt-4" data-id="element-1454" data-mp="an0yj1puo4k7">
          {errors.deliveryMode}
        </p>}

      {draft.deliveryMode && <fieldset disabled={confirmed} className="space-y-6 disabled:opacity-80" data-id="element-1455" data-mp="fng8rkeu4vxy">
          {inPresence ? <>
              <div data-id="element-1456" data-mp="ols8qd1ld632">
                <p className="text-sm font-medium text-slate-700 mb-3" data-id="element-1457" data-mp="omablsireijy">
                  1. Calendarizzare il corso
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-1458" data-mp="1v1uiuw2xiqvl">
                  <Input label="Data inizio" type="date" value={draft.courseStartDate || ''} onChange={e => onChange({
              courseStartDate: e.target.value || undefined
            })} error={errors.courseStartDate} required data-id="element-1459" data-mp="2005u9b8vh9fx" />
                  <Input label="Data fine" type="date" min={draft.courseStartDate} value={draft.courseEndDate || ''} onChange={e => onChange({
              courseEndDate: e.target.value || undefined
            })} error={errors.courseEndDate} data-id="element-1460" data-mp="23i1uolizootu" />
                </div>
              </div>
              <div data-id="element-1461" data-mp="2mbp9dwh5m66p">
                <p className="text-sm font-medium text-slate-700 mb-1" data-id="element-1462" data-mp="309dlaet8u2zm">
                  2. Codice Corso
                </p>
                <p className="text-xs text-slate-500 mb-3" data-id="element-1463" data-mp="2vb2b22z1dqgv">
                  Il corso viene creato manualmente sulla piattaforma esterna di
                  erogazione.
                </p>
                <CreatableSelect label="Codice Corso" value={draft.courseCode || ''} onChange={v => onChange({
            courseCode: v || undefined
          })} options={presenceCodes} onCreate={v => practicesStore.addCourseCode(v)} placeholder="Seleziona dai corsi già inseriti..." newPlaceholder="Inserisci nuovo codice corso" newOptionLabel="+ Inserisci nuovo codice corso..." error={errors.courseCode} required data-id="element-1464" data-mp="3jvp5tc9kf0pd" />
              </div>
              <div data-id="element-1465" data-mp="2r7jjil2v53nf">
                <p className="text-sm font-medium text-slate-700 mb-3" data-id="element-1466" data-mp="2r71diwqefb2b">
                  3. Dati del corso
                </p>
                <Textarea label="Dati del corso" rows={3} value={draft.courseDetails || ''} onChange={e => onChange({
            courseDetails: e.target.value
          })} placeholder="Es. luogo, orari, docente, materiale..." data-id="element-1467" data-mp="2d93485qxq4to" />
              </div>
            </> : <>
              <SearchableSelect label="Codice Corso" value={draft.courseCode || ''} onChange={e => onChange({
          courseCode: e.target.value
        })} options={elearningOptions} placeholder="Cerca il corso già presente sulla piattaforma..." description="Per i corsi On-demand il codice è già presente sulla piattaforma esterna di erogazione" error={errors.courseCode} required data-id="element-1468" data-mp="e01vzcv8ftsk" />
              <div data-id="element-1469" data-mp="sr0xk3xnhi2n">
                <p className="text-sm font-medium text-slate-700 mb-3" data-id="element-1470" data-mp="3idqk0wauqeok">
                  1. Dati dei partecipanti
                </p>
                <div className="space-y-3" data-id="element-1471" data-mp="3de458ifdkqdn">
                  {draft.participants.map(p => <div key={p.id} className="grid grid-cols-1 md:grid-cols-12 gap-3 items-start" data-id="element-1472" data-mp="1sxxrxdbca9ev">
                      <div className="md:col-span-3" data-id="element-1473" data-mp="21xt49lgmsagh">
                        <Input aria-label="Nome partecipante" placeholder="Nome" value={p.firstName} onChange={e => updateParticipant(p.id, {
                  firstName: e.target.value
                })} disabled={Boolean(p.credentialsSentAt)} data-id="element-1474" data-mp="21w9n7sn5hvdn" />
                      </div>
                      <div className="md:col-span-3" data-id="element-1475" data-mp="2pnnerwdh9mxk">
                        <Input aria-label="Cognome partecipante" placeholder="Cognome" value={p.lastName} onChange={e => updateParticipant(p.id, {
                  lastName: e.target.value
                })} disabled={Boolean(p.credentialsSentAt)} data-id="element-1476" data-mp="2pl19k3e27jii" />
                      </div>
                      <div className="md:col-span-4" data-id="element-1477" data-mp="l5fmjvqab7um">
                        <Input aria-label="Email partecipante" type="email" placeholder="Email" value={p.email} onChange={e => updateParticipant(p.id, {
                  email: e.target.value
                })} disabled={Boolean(p.credentialsSentAt)} data-id="element-1478" data-mp="l70ct95kg4mo" />
                      </div>
                      <div className="md:col-span-2 flex items-center justify-between gap-2 md:pt-2" data-id="element-1479" data-mp="3tn3mtlk5i5e6">
                        {p.credentialsSentAt ? <span className="text-xs text-green-700 flex items-center gap-1" title={`Utente ${p.username} · inviate il ${formatDateTime(p.credentialsSentAt)}`} data-id="element-1480" data-mp="18vjkpnkpefe8">
                            <CheckCircle2 className="h-4 w-4" data-id="element-1481" data-mp="1lb182qvo818v" /> Inviate
                          </span> : <span className="text-xs text-slate-400" data-id="element-1482" data-mp="1cbw8dh8di1v4">
                            Da inviare
                          </span>}
                        <Button type="button" variant="light" color="danger" size="sm" className="!px-2" aria-label="Rimuovi partecipante" onClick={() => removeParticipant(p.id)} data-id="element-1483" data-mp="204mkprk6un5x">
                          <Trash2 className="h-4 w-4" data-id="element-1484" data-mp="2iz1khjebqjut" />
                        </Button>
                      </div>
                    </div>)}
                </div>
                {errors.participants && <p className="text-xs text-red-600 font-medium mt-2" data-id="element-1485" data-mp="1sspm0ifljp0f">
                    {errors.participants}
                  </p>}
                <Button type="button" size="sm" variant="flat" color="secondary" className="mt-3" startContent={<Plus className="h-4 w-4" data-id="element-1487" data-mp="151tvdomvmzur" />} onClick={addParticipant} data-id="element-1486" data-mp="1esojyznlibjd">
                  Aggiungi partecipante
                </Button>
              </div>
              <div data-id="element-1488" data-mp="w49vok4nxbm9">
                <p className="text-sm font-medium text-slate-700 mb-3" data-id="element-1489" data-mp="w0m1dire8l6h">
                  2. Credenziali di accesso
                </p>
                <Button type="button" variant="flat" color="secondary" startContent={<KeyRound className="h-4 w-4" data-id="element-1491" data-mp="r32mqhqsa6e0" />} onClick={sendCredentials} disabled={pendingCredentials === 0} data-id="element-1490" data-mp="m3zgitz2hu4g">
                  Genera e invia credenziali
                  {pendingCredentials > 0 && ` (${pendingCredentials})`}
                </Button>
                {errors.credentials && <p className="text-xs text-red-600 font-medium mt-2" data-id="element-1492" data-mp="ch6m6up91ef7">
                    {errors.credentials}
                  </p>}
              </div>
            </>}
        </fieldset>}

      {draft.deliveryMode && <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-5 border-t border-slate-100" data-id="element-1493" data-mp="lhbjgc0qawfn">
          <div className="flex items-center gap-4" data-id="element-1494" data-mp="108c3zfer2gs5">
            {inPresence ? <div className="w-48" data-id="element-1495" data-mp="v86g8ablpefc">
                <Select label="Stato del corso" value={draft.courseStatus || 'Programmato'} onChange={e => onChange({
            courseStatus: e.target.value as PracticeCourseStatus
          })} options={[{
            value: 'Programmato',
            label: 'Programmato'
          }, {
            value: 'Eseguito',
            label: 'Eseguito'
          }]} data-id="element-1496" data-mp="196wdpr2c1g48" />
              </div> : <div data-id="element-1497" data-mp="1qz841ik4tgw0">
                <span className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-1498" data-mp="1qzrph98zqnes">
                  Stato del corso
                </span>
                <span className="inline-flex rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-seform-navy" data-id="element-1499" data-mp="1zxttzyrxny6t">
                  On-demand
                </span>
              </div>}
          </div>
          {confirmed ? <div className="flex items-center gap-3" data-id="element-1500" data-mp="29vrimndpfwzy">
              <span className="text-sm font-medium text-green-700 flex items-center gap-1.5" data-id="element-1501" data-mp="ak6wye7e5owb">
                <CheckCircle2 className="h-4 w-4" data-id="element-1502" data-mp="fi02d50veevj" /> Programmazione confermata
              </span>
              <Button type="button" size="sm" variant="flat" color="default" onClick={() => onChange({
          schedulingConfirmed: false
        })} data-id="element-1503" data-mp="2stb9liecxev8">
                Modifica
              </Button>
            </div> : <Button type="button" onClick={onConfirm} className="sm:min-w-[120px]" data-id="element-1504" data-mp="2f266257vc2fm">
              OK
            </Button>}
        </div>}
    </div>;
}